import { useContext, useState } from 'react';
import { IDocumentUnscoped } from '../../../types/my-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useDocumentUpdate } from '../../hooks/useDocumentUpdate';
import { useQueryClient } from '@tanstack/react-query';
import { AppContext } from '../../../App';
import { AxiosError } from 'axios';
import { useTheme } from '@mui/material/styles';

interface IRemoveTargetTrainingSetProps {
  documentData: IDocumentUnscoped;
}

const RemoveTargetTrainingSet = ({ documentData }: IRemoveTargetTrainingSetProps) => {
  const { setToast } = useContext(AppContext);
  const theme = useTheme();

  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  const queryClient = useQueryClient();

  if (!documentData.target_training_set) {
    return null;
  }

  const { mutateAsync: updateDocument, isPending: isLoadingMutation } = useDocumentUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSets']
      });
      queryClient.invalidateQueries({
        queryKey: ['documents']
      });
      setToast({
        variant: 'success',
        open: true,
        message: 'Documents added successfully.',
      });
    },
    onError: (error: AxiosError) => {
      let message = error.message || 'Sorry, failed to add to training set.';
      setToast({
        variant: 'error',
        open: true,
        message,
      });
    },
  });

  return (
    <>
      {documentData.target_training_set ? (
        <>
          <Tooltip
            placement="top"
            title={`Remove Target Training Set`}
            disableFocusListener
            disableTouchListener
          >
            <ClearIcon
              sx={{ cursor: 'pointer', color: theme.palette.custom.lightGreen }}
              onClick={handleClickOpen}
              data-cy="btn-remove-target-ts"
            />
          </Tooltip>

          <Dialog
            open={open}
            onClose={handleCloseConfirmation}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to remove the target training set?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseConfirmation}>Cancel</Button>
              <Button
                onClick={async () => {
                  await updateDocument({
                    id: documentData.id,
                    data: {
                      target_training_set: null,
                    },
                  });
                }}
                autoFocus
                data-cy="btn-remove-target-ts-modal-yes"
                disabled={isLoadingMutation}
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : null}
    </>
  );
};

export default RemoveTargetTrainingSet;
